import { ChevronDownIcon } from '@heroicons/react/outline';
import { Badge, Pagination, Table } from '@yoast/ui-library';
import { useEffect, useMemo, useState } from 'react';

import type { QueryResultDetailedMentionedBrandsItem } from '../../api/generated/insights.schemas';
import createComponent from '../../components/CreateComponent';

interface BrandsSectionProps {
  id?: string;
  mentionedBrands: QueryResultDetailedMentionedBrandsItem[];
}

const MobileOnly = createComponent`md:yst-hidden yst-mr-2 yst-font-medium yst-text-slate-400 yst-inline-flex yst-w-20`;

const BRANDS_PER_PAGE = 10;

const BrandsSection = ({ id, mentionedBrands }: BrandsSectionProps) => {
  const [brandsPage, setBrandsPage] = useState(1);

  const sortedBrands = useMemo(
    () => [...mentionedBrands].sort((a, b) => a.rank - b.rank),
    [mentionedBrands],
  );

  const brandsTotalPages = Math.ceil(sortedBrands.length / BRANDS_PER_PAGE);
  const brandsStartIndex = (brandsPage - 1) * BRANDS_PER_PAGE;
  const brandsEndIndex = brandsStartIndex + BRANDS_PER_PAGE;
  const currentBrands = sortedBrands.slice(brandsStartIndex, brandsEndIndex);

  useEffect(() => {
    if (brandsPage > brandsTotalPages && brandsTotalPages > 0) {
      setBrandsPage(1);
    }
  }, [brandsTotalPages, brandsPage]);

  if (mentionedBrands.length === 0) {
    return null;
  }

  return (
    <details id={id} className="yst-group/brands yst-mt-6" open>
      <summary className="yst-flex yst-items-center yst-justify-between yst-cursor-pointer yst-list-none [&::-webkit-details-marker]:yst-hidden yst-mb-4">
        <h3 className="yst-font-semibold">Brands ({mentionedBrands.length})</h3>
        <ChevronDownIcon className="yst-h-4 yst-w-4 yst-text-gray-400 yst-flex-shrink-0 group-open/brands:yst-rotate-180 yst-transition-transform" />
      </summary>
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header className="yst-hidden md:yst-table-cell">{'\u00A0'}</Table.Header>
            <Table.Header className="yst-hidden md:yst-table-cell">Brand</Table.Header>
            <Table.Header className="yst-hidden md:yst-table-cell">Context</Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body className="max-md:yst-border-t-0">
          {currentBrands.map((brand) => (
            <tr key={`${brand.id}-${brand.rank}`} className="yst-flex yst-flex-col yst-py-4 md:yst-table-row md:yst-py-0">
              <Table.Cell className="yst-px-4 yst-py-1 md:yst-p-2 md:yst-pl-4">
                <MobileOnly as="span">Rank:</MobileOnly>
                #{brand.rank}
              </Table.Cell>
              <Table.Cell className="yst-px-4 yst-py-1 md:yst-p-2">
                <MobileOnly as="span">Brand:</MobileOnly>
                <Badge variant={brand.isSelf ? 'ai' : 'plain'}>
                  {brand.name}
                </Badge>
              </Table.Cell>
              <Table.Cell className="yst-px-4 yst-py-1 yst-text-sm yst-text-gray-600 yst-italic md:yst-p-2">
                <MobileOnly as="span" className="yst-not-italic">Context:</MobileOnly>
                "{brand.context}"
              </Table.Cell>
            </tr>
          ))}
        </Table.Body>
      </Table>
      {sortedBrands.length > BRANDS_PER_PAGE && (
        <div className="yst-flex yst-items-center yst-justify-end yst-gap-4 yst-mt-4">
          <p className="yst-mb-0">
            Showing {brandsStartIndex + 1} to{' '}
            {brandsEndIndex > sortedBrands.length ? sortedBrands.length : brandsEndIndex} of{' '}
            {sortedBrands.length} results
          </p>
          <Pagination
            variant="text"
            className="yst-flex-row yst-gap-0"
            current={brandsPage}
            total={brandsTotalPages}
            onNavigate={setBrandsPage}
            screenReaderTextPrevious="Previous page"
            screenReaderTextNext="Next page"
          />
        </div>
      )}
    </details>
  );
};

export default BrandsSection;
